// Copied from flowsjs `packages/ui/kit/src/StatusBadge.tsx` (`@aiuirun/ui-kit`), which is not published.

import { Box } from "@mui/material";
import { useTokens } from "../theme/theme";
import type { DesignTokens } from "../theme/tokens";

// A design status badge: soft-tinted pill + leading dot + on-soft ink text (per the design's comp-badges).
// Colors come from the active theme (useTokens), so it renders correctly in both light and dark.

export type BadgeTone = "success" | "warning" | "danger" | "info" | "accent" | "neutral";

export type StatusBadgeProps = {
  label: string;
  tone?: BadgeTone;
  /** Show the leading dot (ignored for the neutral tone, which has none). */
  dot?: boolean;
};

// bg = soft token, fg = on-soft ink token, dot (led) = saturated base token
function toneStyle(
  t: DesignTokens,
  tone: BadgeTone,
): { bg: string; fg: string; dot: string | null } {
  switch (tone) {
    case "success":
      return { bg: t.semantic.successSoft, fg: t.semantic.successInk, dot: t.semantic.success };
    case "warning":
      return { bg: t.semantic.warningSoft, fg: t.semantic.warningInk, dot: t.semantic.warning };
    case "danger":
      return { bg: t.semantic.dangerSoft, fg: t.semantic.dangerInk, dot: t.semantic.danger };
    case "info":
      return { bg: t.semantic.infoSoft, fg: t.semantic.infoInk, dot: t.semantic.info };
    case "accent":
      return { bg: t.accent.soft, fg: t.accent.ink, dot: t.accent.base };
    default:
      return { bg: t.surface.cardSunken, fg: t.ink.muted, dot: null };
  }
}

export function StatusBadge({ label, tone = "neutral", dot = true }: StatusBadgeProps) {
  const t = useTokens();
  const s = toneStyle(t, tone);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "11.5px",
        fontWeight: 500,
        letterSpacing: "0.005em",
        lineHeight: 1.5, // design .badge inherits body line-height 1.5 — this drives the pill height
        py: "3px",
        px: "10px",
        borderRadius: `${t.radius.pill}px`,
        bgcolor: s.bg,
        color: s.fg,
      }}
    >
      {dot && s.dot && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.dot, flex: "none" }}
        />
      )}
      {label}
    </Box>
  );
}
